import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { IconSearch } from '@/components/icons';

/** Vertical stack with panel spacing tokens. */
export function PanelStack({
  children,
  className = '',
  tight = false,
}: {
  children: ReactNode;
  className?: string;
  tight?: boolean;
}) {
  return (
    <div className={`panel-stack ${tight ? 'tight' : ''} ${className}`.trim()}>{children}</div>
  );
}

/** Labeled control with optional hint under the input (never between siblings). */
export function PanelField({
  label,
  hint,
  children,
  className = '',
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`field panel-field ui-field ${className}`.trim()}>
      <span>{label}</span>
      {children}
      {hint ? <small className="field-hint">{hint}</small> : null}
    </label>
  );
}

export function PanelInput({
  label,
  hint,
  inputMode,
  dir,
  lang,
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; className?: string }) {
  const numeric = inputMode === 'decimal' || inputMode === 'numeric' || inputMode === 'tel';
  return (
    <PanelField label={label} hint={hint} className={className}>
      <input
        className="ui-control"
        inputMode={inputMode}
        dir={dir ?? (numeric ? 'ltr' : undefined)}
        lang={lang ?? (numeric ? 'en' : undefined)}
        {...rest}
      />
    </PanelField>
  );
}

export function PanelSelect({
  label,
  value,
  onChange,
  options,
  required,
  disabled,
  hint,
  className = '',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  disabled?: boolean;
  hint?: string;
  className?: string;
}) {
  return (
    <PanelField label={label} hint={hint} className={className}>
      <select
        className="ui-control"
        value={value}
        required={required}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </PanelField>
  );
}

export function PanelSearch({
  value,
  onChange,
  placeholder = 'بحث...',
  onSubmit,
  className = '',
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  onSubmit?: () => void;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`pro-search panel-search ui-search ${className}`.trim()}>
      <IconSearch size={18} aria-hidden />
      <input
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit?.();
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={placeholder}
      />
    </label>
  );
}

export function PanelButton({
  children,
  variant = 'primary',
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' }) {
  const variantClass =
    variant === 'primary' ? 'btn btn-primary' : variant === 'danger' ? 'btn btn-danger' : 'btn btn-secondary';
  return (
    <button type="button" className={`${variantClass} ${className}`.trim()} {...rest}>
      {children}
    </button>
  );
}

export function PanelThumb({
  src,
  label,
  fallback,
}: {
  src?: string;
  label?: string;
  fallback?: ReactNode;
}) {
  return (
    <div className="image-thumb" title={label}>
      {src ? <img src={src} alt={label ?? ''} loading="lazy" /> : (fallback ?? null)}
    </div>
  );
}
